import type { DeployAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { WarchestState } from "../types/game-state";
import { isValidHex } from "../utils/hex-utils";
import { hasCoin } from "../utils/zone-utils";

export function validateDeploy(
  state: WarchestState,
  action: DeployAction,
): ValidationResult {
  if (state.phase !== "USE_COINS") {
    return { valid: false, error: "Not in USE_COINS phase" };
  }
  if (action.player !== state.activePlayer) {
    return { valid: false, error: "Not your turn" };
  }
  const zones = state.playerZones[action.player];
  if (!hasCoin(zones, action.coinType, "hand")) {
    return { valid: false, error: "Coin not in hand" };
  }
  const unitDef = state.config.units[action.coinType];
  if (!unitDef) {
    return { valid: false, error: "Unknown coin type" };
  }
  if (action.coinType === "ROYAL") {
    return { valid: false, error: "Royal Coin cannot deploy a unit" };
  }
  const boardDef = state.config.boards[state.boardId];
  if (!boardDef || !isValidHex(boardDef, action.targetHex)) {
    return { valid: false, error: "Invalid target hex" };
  }
  if (unitDef.attributeId === "SCOUT_FLEXIBLE_DEPLOY") {
    const isAdjacentToFriendly = (
      boardDef.adjacency[action.targetHex] ?? []
    ).some((adjHex) => {
      const adjCell = state.board[adjHex];
      return (
        adjCell !== null &&
        adjCell !== undefined &&
        adjCell.owner === action.player
      );
    });
    if (!isAdjacentToFriendly) {
      return {
        valid: false,
        error: "Scout must deploy adjacent to a friendly unit",
      };
    }
    if (state.board[action.targetHex] !== null) {
      return { valid: false, error: "Location is occupied" };
    }
    return { valid: true };
  }
  const location = state.locations[action.targetHex];
  if (!location) {
    return { valid: false, error: "Target hex is not a location" };
  }
  const isOwnedLocation = location.controlledBy === action.player;
  const isAdjacentToFriendlyUnit =
    !isOwnedLocation &&
    (boardDef.adjacency[action.targetHex] ?? []).some((adjHex) => {
      const adjCell = state.board[adjHex];
      return (
        adjCell !== null &&
        adjCell !== undefined &&
        adjCell.owner === action.player
      );
    });
  if (!isOwnedLocation && !isAdjacentToFriendlyUnit) {
    return {
      valid: false,
      error:
        "Location must be controlled by you or adjacent to one of your units",
    };
  }
  if (state.board[action.targetHex] !== null) {
    return { valid: false, error: "Location is occupied" };
  }
  const deployedCount = Object.values(state.board).filter(
    (cell) =>
      cell !== null &&
      cell.owner === action.player &&
      cell.unitType === action.coinType,
  ).length;
  if (unitDef.attributeId === "FOOTMAN_DUAL") {
    if (deployedCount >= 2) {
      return { valid: false, error: "A unit of this type is already deployed" };
    }
  } else if (deployedCount >= 1) {
    return { valid: false, error: "A unit of this type is already deployed" };
  }
  return { valid: true };
}
